import DemoLayout from "@/components/demo/DemoLayout";
import { Lock, BarChart3, Shield, TrendingUp, Users, Target } from "lucide-react";
import { useParams, useLocation } from "react-router-dom";

const FEATURES = {
  "talent-strategy": {
    title: "Talent Strategy",
    threshold: 75,
    thresholdLabel: "75% SA completion",
    icon: Target,
    unlocks: [
      { icon: Users, title: "Builder Core Analysis", desc: "Identify top talent by segment" },
      { icon: Shield, title: "Flight Risk Scoring", desc: "Predict retention risks" },
      { icon: TrendingUp, title: "Leadership Pipeline", desc: "Succession readiness tracking" },
    ],
  },
  "workforce-planning": {
    title: "Workforce Planning",
    threshold: 50,
    thresholdLabel: "50% MV completion",
    icon: BarChart3,
    isMV: true,
    unlocks: [
      { icon: Users, title: "Role Architecture", desc: "Skills mapping by role" },
      { icon: Target, title: "Skills Gap Analysis", desc: "Targeted development priorities" },
      { icon: BarChart3, title: "Capacity Planning", desc: "Workforce composition modeling" },
    ],
  },
  roi: {
    title: "ROI Analysis",
    threshold: 100,
    thresholdLabel: "100% assessment completion",
    icon: TrendingUp,
    unlocks: [
      { icon: TrendingUp, title: "Investment Recommendations", desc: "Data-driven development spend" },
      { icon: BarChart3, title: "Cost Modeling", desc: "Replacement vs development cost" },
      { icon: Shield, title: "Impact Forecasting", desc: "Projected ROI on talent actions" },
    ],
  },
};

export default function LockedFeature() {
  const location = useLocation();
  const featureKey = location.pathname.split("/").pop();
  const feature = FEATURES[featureKey] || FEATURES["talent-strategy"];
  const currentRate = 35.5; // Would come from API in real implementation
  const Icon = feature.icon;

  return (
    <DemoLayout>
      <div className="flex items-center justify-center min-h-[70vh]" data-testid="locked-feature">
        <div className="max-w-xl w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "var(--surface-muted)" }}>
              <Lock className="w-8 h-8" style={{ color: "var(--text-muted)" }} />
            </div>
          </div>

          <div>
            <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>
              {feature.title} unlocks at {feature.thresholdLabel}
            </h1>
            <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
              Complete more assessments to unlock strategic insights
            </p>
          </div>

          <div className="card-m p-4">
            <div className="progress-m mb-2">
              <div className="progress-fill" style={{
                width: `${Math.min((currentRate / feature.threshold) * 100, 100)}%`,
                background: "var(--color-accent)",
              }} />
            </div>
            <div className="flex justify-between text-[11px]" style={{ color: "var(--text-muted)" }}>
              <span>{currentRate}% current</span>
              <span>Target: {feature.threshold}%</span>
            </div>
          </div>

          <div style={{ borderTop: "var(--border-width) solid var(--border-default)", paddingTop: 24 }}>
            <div className="text-[11px] font-semibold tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>
              WHAT UNLOCKS AT {feature.thresholdLabel.toUpperCase()}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {feature.unlocks.map((u) => {
                const UIcon = u.icon;
                return (
                  <div key={u.title} className="card-m p-4 text-center">
                    <UIcon className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--text-muted)" }} />
                    <div className="text-[12px] font-medium" style={{ color: "var(--text-primary)" }}>{u.title}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>{u.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}
